import {
  MdDone,
  MdDoneAll,
  MdRestaurant,
  MdCake,
  MdGroups,
  MdShoppingBasket,
  MdLocalOffer,
  MdReceiptLong,
  MdCheckCircle,
  MdCancel,
  MdArrowForward,
  MdShoppingCartCheckout,
  MdEditNote,
} from "react-icons/md";

import {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import API from "../../../services/api";
import getImageUrl from "../../../utils/imageUrl";

import "./Message.css";


function Message({
  message,
  currentUserId,
}) {

  // =========================================================
  // SAFE MESSAGE
  // IMPORTANT:
  // We keep hooks unconditional.
  // =========================================================

  const safeMessage =
    message || {};


  // =========================================================
  // NAVIGATION
  // =========================================================

  const navigate =
    useNavigate();


  // =========================================================
  // ORDER ACTION STATE
  // =========================================================

  const [
    orderActionLoading,
    setOrderActionLoading,
  ] = useState(false);


  const [
    orderActionError,
    setOrderActionError,
  ] = useState("");


  // =========================================================
  // LIVE CUSTOM ORDER
  // =========================================================

  const [
    liveCustomOrder,
    setLiveCustomOrder,
  ] = useState(null);


  const [
    customOrderLoading,
    setCustomOrderLoading,
  ] = useState(false);


  // =========================================================
  // CURRENT USER
  // =========================================================

  const getCurrentUser = () => {

    try {

      const storedUser =
        localStorage.getItem("user");


      if (!storedUser) {

        return null;

      }


      return JSON.parse(
        storedUser
      );

    } catch (error) {

      console.error(
        "Message User Parse Error:",
        error
      );

      return null;

    }

  };


  const currentUser =
    getCurrentUser();


  const currentUserRole =
    currentUser?.role;


  const isProvider =
    currentUserRole === "provider";


  const isCustomer =
    currentUserRole === "customer";


  // =========================================================
  // SENDER
  // =========================================================

  const senderId =
    safeMessage.senderId?._id ||
    safeMessage.senderId;


  const isOwnMessage =
    senderId?.toString() ===
    currentUserId?.toString();


  // =========================================================
  // MESSAGE TYPE
  // =========================================================

  const messageType =
    safeMessage.messageType ||
    "text";


  // =========================================================
  // REQUEST DETAILS
  // =========================================================

  const request =
    safeMessage.requestDetails ||
    {};


  // =========================================================
  // ORDER DETAILS
  // =========================================================

  const orderDetails =
    safeMessage.orderDetails ||
    {};


  // =========================================================
  // LIVE CUSTOM ORDER
  //
  // This keeps the customer UI updated when provider
  // adds price / accepts / rejects without requiring
  // another chat message.
  // =========================================================

  useEffect(() => {

    const isCustomOrder =
      messageType === "custom_order_request" ||
      messageType === "custom_order_price" ||
      messageType === "custom_order_accepted" ||
      messageType === "custom_order_rejected";


    const customOrderId =
      safeMessage.orderDetails?.orderId ||
      safeMessage.orderDetails?._id ||
      safeMessage.customOrderId ||
      null;


    if (
      !isCustomOrder ||
      !customOrderId
    ) {

      return undefined;

    }


    let mounted = true;


    const loadCustomOrder =
      async () => {

        try {

          setCustomOrderLoading(
            true
          );


          const response =
            await API.get(
              `/custom-orders/customer/${customOrderId}`
            );


          console.log(
            "🟢 LIVE CUSTOM ORDER RESPONSE:",
            response.data
          );


          if (
            mounted &&
            response.data?.success
          ) {

            const order =
              response.data.order;


            console.log(
              "🟢 LIVE CUSTOM ORDER:",
              order
            );


            setLiveCustomOrder(
              order
            );

          }

        } catch (error) {

          console.error(
            "❌ Load Live Custom Order Error:",
            error.response?.data ||
            error
          );

        } finally {

          if (mounted) {

            setCustomOrderLoading(
              false
            );

          }

        }

      };


    // Initial load

    loadCustomOrder();


    // =====================================================
    // REFRESH WHEN TAB GETS FOCUS
    // =====================================================

    const handleFocus =
      () => {

        loadCustomOrder();

      };


    window.addEventListener(
      "focus",
      handleFocus
    );


    // =====================================================
    // POLLING
    // =====================================================

    const interval =
      setInterval(
        loadCustomOrder,
        5000
      );


    // =====================================================
    // CLEANUP
    // =====================================================

    return () => {

      mounted = false;


      clearInterval(
        interval
      );


      window.removeEventListener(
        "focus",
        handleFocus
      );

    };

  }, [
    messageType,
    safeMessage.orderDetails?.orderId,
    safeMessage.orderDetails?._id,
    safeMessage.customOrderId,
  ]);


  // =========================================================
  // EMPTY MESSAGE
  //
  // Safe because all hooks above have already executed.
  // =========================================================

  if (!message) {

    return null;

  }


  // =========================================================
  // TIME
  // =========================================================

  const formatTime =
    (date) => {

      if (!date) {

        return "";

      }


      const messageDate =
        new Date(date);


      if (
        Number.isNaN(
          messageDate.getTime()
        )
      ) {

        return "";

      }


      return messageDate.toLocaleTimeString(
        [],
        {
          hour: "2-digit",
          minute: "2-digit",
        }
      );

    };


  // =========================================================
  // ORDER STATUS
  // =========================================================

  const orderStatus =
    orderDetails.status ||
    "pending";


  // =========================================================
  // REQUEST ICON
  // =========================================================

  const getRequestIcon =
    () => {

      switch (messageType) {

        case "food_request":

          return (
            <MdRestaurant />
          );


        case "cake_request":

          return (
            <MdCake />
          );


        case "catering_request":

          return (
            <MdGroups />
          );


        case "provision_request":

          return (
            <MdShoppingBasket />
          );


        case "offer":

          return (
            <MdLocalOffer />
          );


        case "order_request":

          return (
            <MdReceiptLong />
          );


        default:

          return null;

      }

    };


  // =========================================================
  // REQUEST TITLE
  // =========================================================

  const getRequestTitle =
    () => {

      switch (messageType) {

        case "food_request":

          return "Food Request";


        case "cake_request":

          return "Cake Request";


        case "catering_request":

          return "Catering Request";


        case "provision_request":

          return "Provision Request";


        case "offer":

          return "Special Offer";


        case "order_request":

          return "Order Request";


        default:

          return "";

      }

    };


  // =========================================================
  // READ STATUS
  // =========================================================

  const renderReadStatus =
    () => {

      if (!isOwnMessage) {

        return null;

      }


      if (safeMessage.isRead) {

        return (

          <MdDoneAll
            className="message-read read"
            title="Read"
          />

        );

      }


      return (

        <MdDone
          className="message-read"
          title="Sent"
        />

      );

    };


  // =========================================================
  // IMAGE MESSAGE
  // =========================================================

  const renderImageMessage =
    () => {

      const attachments =
        Array.isArray(
          safeMessage.attachments
        )
          ? safeMessage.attachments
          : [];


      const imageAttachments =
        attachments.filter(
          attachment =>
            attachment?.type === "image"
        );


      if (
        imageAttachments.length === 0
      ) {

        return null;

      }


      return (

        <div className="message-images">

          {imageAttachments.map(
            (
              attachment,
              index
            ) => {

              if (
                !attachment?.url
              ) {

                return null;

              }


              return (

               <img
  key={`${attachment.url}-${index}`}
  src={
    getImageUrl(attachment.url) ||
    "/images/default-food.jpg"
  }
  alt={
    attachment.name ||
    "Message attachment"
  }
  className="message-image"
  loading="lazy"
  onError={(event) => {
    event.currentTarget.src =
      "/images/default-food.jpg";
  }}
/>

              );

            }
          )}

        </div>

      );

    };


  // =========================================================
  // REQUEST MESSAGE
  // =========================================================

  const renderRequestMessage =
    () => {

      return (

        <div className="request-message">

          {/* HEADER */}

          <div className="request-header">

            <div className="request-icon">

              {getRequestIcon()}

            </div>


            <div>

              <h4>
                {getRequestTitle()}
              </h4>


              <span>
                HomeFoods Request
              </span>

            </div>

          </div>


          {/* DETAILS */}

          <div className="request-details">

            {request.itemName && (

              <div className="request-row">

                <span>
                  Item
                </span>


                <strong>
                  {request.itemName}
                </strong>

              </div>

            )}


            {request.quantity !== null &&
              request.quantity !== undefined && (

                <div className="request-row">

                  <span>
                    Quantity
                  </span>


                  <strong>

                    {request.quantity}{" "}

                    {request.unit || ""}

                  </strong>

                </div>

              )}


            {request.eventDate && (

              <div className="request-row">

                <span>
                  Event Date
                </span>


                <strong>

                  {new Date(
                    request.eventDate
                  ).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }
                  )}

                </strong>

              </div>

            )}


            {request.peopleCount !== null &&
              request.peopleCount !== undefined && (

                <div className="request-row">

                  <span>
                    People
                  </span>


                  <strong>
                    {request.peopleCount}
                  </strong>

                </div>

              )}

          </div>


          {/* DESCRIPTION */}

          {request.description && (

            <div className="request-description">

              {request.description}

            </div>

          )}

        </div>

      );

    };


  // =========================================================
  // OFFER MESSAGE
  // =========================================================

  const renderOfferMessage =
    () => {

      return (

        <div className="offer-message">

          <div className="offer-icon">

            <MdLocalOffer />

          </div>


          <div>

            <span>
              Special Offer
            </span>


            <strong>

              {safeMessage.message ||
                "Special offer available"}

            </strong>

          </div>

        </div>

      );

    };


  // =========================================================
  // NORMAL ORDER TOTAL
  // =========================================================

  const getOrderTotal =
    () => {

      if (
        Number(
          orderDetails.totalAmount
        ) > 0
      ) {

        return Number(
          orderDetails.totalAmount
        );

      }


      if (
        !Array.isArray(
          orderDetails.items
        )
      ) {

        return 0;

      }


      return orderDetails.items.reduce(
        (
          total,
          item
        ) => {

          const price =
            Number(
              item.price
            ) || 0;


          const quantity =
            Number(
              item.quantity
            ) || 1;


          return (
            total +
            price *
            quantity
          );

        },
        0
      );

    };


  // =========================================================
  // ACCEPT NORMAL ORDER
  // =========================================================

  const handleAcceptOrder =
    async () => {

      if (
        orderActionLoading ||
        orderStatus !== "pending"
      ) {

        return;

      }


      try {

        setOrderActionLoading(
          true
        );


        setOrderActionError(
          ""
        );


        const response =
          await API.post(
            `/messages/${safeMessage._id}/accept`
          );


        if (
          !response.data?.success
        ) {

          throw new Error(
            response.data?.message ||
            "Failed to accept order"
          );

        }


        console.log(
          "✅ Order accepted:",
          response.data
        );

      } catch (error) {

        console.error(
          "Accept Order Error:",
          error
        );


        setOrderActionError(
          error.response?.data?.message ||
          error.message ||
          "Failed to accept order"
        );

      } finally {

        setOrderActionLoading(
          false
        );

      }

    };


  // =========================================================
  // REJECT NORMAL ORDER
  // =========================================================

  const handleRejectOrder =
    async () => {

      if (
        orderActionLoading ||
        orderStatus !== "pending"
      ) {

        return;

      }


      try {

        setOrderActionLoading(
          true
        );


        setOrderActionError(
          ""
        );


        const response =
          await API.post(
            `/messages/${safeMessage._id}/reject`
          );


        if (
          !response.data?.success
        ) {

          throw new Error(
            response.data?.message ||
            "Failed to reject order"
          );

        }


        console.log(
          "❌ Order rejected:",
          response.data
        );

      } catch (error) {

        console.error(
          "Reject Order Error:",
          error
        );


        setOrderActionError(
          error.response?.data?.message ||
          error.message ||
          "Failed to reject order"
        );

      } finally {

        setOrderActionLoading(
          false
        );

      }

    };


  // =========================================================
  // NORMAL ORDER CHECKOUT
  // =========================================================

  const handleCheckout =
    () => {

      navigate(
        "/cart",
        {

          state: {

            acceptedOrder: {

              orderId:
                orderDetails.orderId ||
                null,


              items:
                Array.isArray(
                  orderDetails.items
                )
                  ? orderDetails.items
                  : [],


              totalAmount:
                Number(
                  orderDetails.totalAmount
                ) || 0,


              messageId:
                safeMessage._id,

            },

          },

        }
      );

    };


  // =========================================================
  // NORMAL ORDER ITEMS
  // =========================================================

  const renderOrderItems =
    () => {

      if (
        !Array.isArray(
          orderDetails.items
        ) ||
        orderDetails.items.length === 0
      ) {

        return null;

      }


      return (

        <div className="order-items">

          {orderDetails.items.map(
            (
              item,
              index
            ) => {

              const quantity =
                Number(
                  item.quantity
                ) || 1;


              const price =
                Number(
                  item.price
                ) || 0;


              return (

                <div
                  className="order-item-row"
                  key={
                    item.foodId ||
                    `${item.foodName}-${index}`
                  }
                >

                  <div>

                    <strong>
                      {item.foodName}
                    </strong>


                    <span>
                      × {quantity}
                    </span>

                  </div>


                  <strong>

                    ₹
                    {(
                      price *
                      quantity
                    ).toFixed(0)}

                  </strong>

                </div>

              );

            }
          )}

        </div>

      );

    };


  // =========================================================
  // NORMAL ORDER MESSAGE
  // =========================================================

  const renderOrderMessage =
    () => {

      const isPending =
        orderStatus === "pending";


      const isAccepted =
        orderStatus === "accepted";


      const isRejected =
        orderStatus === "rejected";


      return (

        <div
          className={`order-message ${
            isAccepted
              ? "order-accepted"
              : ""
          } ${
            isRejected
              ? "order-rejected"
              : ""
          }`}
        >

          {/* HEADER */}

          <div className="order-header">

            <div className="order-icon">

              {isAccepted ? (

                <MdCheckCircle />

              ) : isRejected ? (

                <MdCancel />

              ) : (

                <MdReceiptLong />

              )}

            </div>


            <div>

              <h4>

                {isAccepted
                  ? "Order Accepted"
                  : isRejected
                    ? "Order Rejected"
                    : "Order Request"}

              </h4>


              <span>
                HomeFoods Order
              </span>

            </div>

          </div>


          {/* ITEMS */}

          {renderOrderItems()}


          {/* FALLBACK MESSAGE */}

          {(
            !Array.isArray(
              orderDetails.items
            ) ||
            orderDetails.items.length === 0
          ) &&
            safeMessage.message && (

              <p className="order-description">

                {safeMessage.message}

              </p>

            )}


          {/* TOTAL */}

          {isPending &&
            getOrderTotal() > 0 && (

              <div className="order-total">

                <span>
                  Total
                </span>


                <strong>

                  ₹
                  {getOrderTotal().toFixed(0)}

                </strong>

              </div>

            )}


          {/* PROVIDER ACCEPT / REJECT */}

          {isProvider &&
            isPending && (

              <div className="order-actions">

                <button
                  type="button"
                  className="order-reject-button"
                  onClick={
                    handleRejectOrder
                  }
                  disabled={
                    orderActionLoading
                  }
                >

                  <MdCancel />


                  {orderActionLoading
                    ? "Processing..."
                    : "Reject"}

                </button>


                <button
                  type="button"
                  className="order-accept-button"
                  onClick={
                    handleAcceptOrder
                  }
                  disabled={
                    orderActionLoading
                  }
                >

                  <MdCheckCircle />


                  {orderActionLoading
                    ? "Processing..."
                    : "Accept"}

                </button>

              </div>

            )}


          {/* PROVIDER ACCEPTED */}

          {isProvider &&
            isAccepted && (

              <div className="order-status-success">

                <MdCheckCircle />


                <span>
                  Accepted this order
                </span>

              </div>

            )}


          {/* PROVIDER REJECTED */}

          {isProvider &&
            isRejected && (

              <div className="order-status-rejected">

                <MdCancel />


                <span>
                  Rejected this order
                </span>

              </div>

            )}


          {/* CUSTOMER ACCEPTED */}

          {isCustomer &&
            isAccepted && (

              <div className="customer-checkout-section">

                <div className="customer-checkout-message">

                  <MdCheckCircle />


                  <span>
                    Your order has been accepted.
                  </span>

                </div>


                <button
                  type="button"
                  className="proceed-checkout-button"
                  onClick={
                    handleCheckout
                  }
                >

                  <span>
                    Proceed to Checkout
                  </span>


                  <MdArrowForward />

                </button>

              </div>

            )}


          {/* CUSTOMER REJECTED */}

          {isCustomer &&
            isRejected && (

              <div className="customer-order-rejected">

                <MdCancel />


                <span>

                  Sorry, this order request
                  was rejected by the provider.

                </span>

              </div>

            )}


          {/* ERROR */}

          {orderActionError && (

            <div className="order-action-error">

              {orderActionError}

            </div>

          )}

        </div>

      );

    };


  // =========================================================
  // CUSTOM ORDER MESSAGE
  // =========================================================

  const renderCustomOrderMessage =
    () => {

      // =====================================================
      // LIVE ORDER
      // =====================================================

      const liveOrder =
        liveCustomOrder;


      // =====================================================
      // CATERING DETECTION
      //
      // Catering can be identified by:
      // providerType
      // orderType
      // cateringDetails
      // menuItems
      // =====================================================

      const isCateringOrder =

        liveOrder?.providerType ===
          "Catering"

        ||

        orderDetails?.providerType ===
          "Catering"

        ||

        liveOrder?.orderType ===
          "catering"

        ||

        orderDetails?.orderType ===
          "catering"

        ||

        Boolean(
          liveOrder?.cateringDetails
        )

        ||

        Boolean(
          orderDetails?.cateringDetails
        )

        ||

        Array.isArray(
          liveOrder?.menuItems
        )

        ||

        Array.isArray(
          liveOrder?.cateringDetails?.menuItems
        )

        ||

        Array.isArray(
          orderDetails?.menuItems
        )

        ||

        Array.isArray(
          orderDetails?.cateringDetails?.menuItems
        );


      // =====================================================
      // STATUS
      // =====================================================

      const backendStatus =
        liveOrder?.customOrderStatus ||
        liveOrder?.status ||
        orderDetails?.customOrderStatus ||
        orderDetails?.status ||
        "Pending Provider Review";


      // =====================================================
      // NORMALIZE STATUS
      // =====================================================

      const normalizedStatusMap = {

        "Pending Provider Review":
          "pending",

        "Provider Reviewing":
          "reviewing",

        "Price Added":
          "price_added",

        "Accepted":
          "accepted",

        "Rejected":
          "rejected",

        "Cancelled":
          "rejected",

      };


      const normalizedStatus =
        normalizedStatusMap[
          backendStatus
        ] ||

        backendStatus
          ?.toString()
          .toLowerCase()
          .replaceAll(
            " ",
            "_"
          );


      const isPending =
        normalizedStatus ===
        "pending";


      const isReviewing =
        normalizedStatus ===
        "reviewing";


      const isPriceAdded =
        normalizedStatus ===
        "price_added";


      const isAccepted =
        normalizedStatus ===
        "accepted";


      const isRejected =
        normalizedStatus ===
        "rejected";


      // =====================================================
      // CATERING DETAILS OBJECT
      // =====================================================

      const cateringDetails =

        liveOrder?.cateringDetails ||

        liveOrder?.customOrderDetails ||

        orderDetails?.cateringDetails ||

        orderDetails?.customOrderDetails ||

        {};


      // =====================================================
      // FUNCTION NAME
      // =====================================================

      const cateringFunctionName =

        liveOrder?.functionName ||

        cateringDetails?.functionName ||

        orderDetails?.functionName ||

        "";


      // =====================================================
      // FUNCTION DATE
      // =====================================================

      const cateringFunctionDate =

        liveOrder?.functionDate ||

        cateringDetails?.functionDate ||

        orderDetails?.functionDate ||

        "";


      // =====================================================
      // GUESTS
      // =====================================================

      const cateringGuests =
        Number(

          liveOrder?.guests ??

          cateringDetails?.guests ??

          orderDetails?.guests ??

          0

        ) || 0;


      // =====================================================
      // ITEMS
      //
      // IMPORTANT:
      // Search all possible backend structures.
      // =====================================================

      let items = [];


      if (isCateringOrder) {

        const cateringItemCandidates = [

          // Most direct structure

          liveOrder?.menuItems,


          // Catering nested structure

          liveOrder?.cateringDetails?.menuItems,


          // Custom order nested structure

          liveOrder?.customOrderDetails?.menuItems,


          // Message order details

          orderDetails?.menuItems,


          // Message catering details

          orderDetails?.cateringDetails?.menuItems,


          // Message custom details

          orderDetails?.customOrderDetails?.menuItems,

        ];


        const foundCateringItems =
          cateringItemCandidates.find(
            candidate =>
              Array.isArray(candidate) &&
              candidate.length > 0
          );


        items =
          Array.isArray(
            foundCateringItems
          )
            ? foundCateringItems
            : [];

      } else {

        // =================================================
        // NORMAL CUSTOM ORDER
        // =================================================

        const normalItemCandidates = [

          liveOrder?.items,

          liveOrder?.customOrderDetails?.items,

          orderDetails?.items,

          orderDetails?.customOrderDetails?.items,

        ];


        const foundNormalItems =
          normalItemCandidates.find(
            candidate =>
              Array.isArray(candidate) &&
              candidate.length > 0
          );


        items =
          Array.isArray(
            foundNormalItems
          )
            ? foundNormalItems
            : [];

      }


      // =====================================================
      // DEBUG
      // =====================================================

      console.log(
        "========================================"
      );

      console.log(
        "🍽️ CUSTOM ORDER MESSAGE"
      );

      console.log(
        "Message Type:",
        messageType
      );

      console.log(
        "Live Order:",
        liveOrder
      );

      console.log(
        "Order Details:",
        orderDetails
      );

      console.log(
        "Is Catering:",
        isCateringOrder
      );

      console.log(
        "Catering Details:",
        cateringDetails
      );

      console.log(
        "Catering Function:",
        cateringFunctionName
      );

      console.log(
        "Catering Date:",
        cateringFunctionDate
      );

      console.log(
        "Catering Guests:",
        cateringGuests
      );

      console.log(
        "Final Menu Items:",
        items
      );

      console.log(
        "========================================"
      );


      // =====================================================
      // PRICING
      // =====================================================

      const subtotal =
        Number(
          liveOrder?.subTotal ??
          liveOrder?.subtotal ??
          orderDetails?.subTotal ??
          orderDetails?.subtotal ??
          0
        ) || 0;


      const gst =
        Number(
          liveOrder?.gst ??
          orderDetails?.gst ??
          0
        ) || 0;


      const packingCharge =
        Number(
          liveOrder?.packingCharge ??
          orderDetails?.packingCharge ??
          0
        ) || 0;


      const deliveryCharge =
        Number(
          liveOrder?.deliveryCharge ??
          orderDetails?.deliveryCharge ??
          0
        ) || 0;


      const platformFee =
        Number(
          liveOrder?.platformFee ??
          orderDetails?.platformFee ??
          0
        ) || 0;


      const discount =
        Number(
          liveOrder?.discount ??
          orderDetails?.discount ??
          0
        ) || 0;


      const grandTotal =
        Number(
          liveOrder?.grandTotal ??
          liveOrder?.totalAmount ??
          orderDetails?.grandTotal ??
          orderDetails?.totalAmount ??
          0
        ) || 0;


      // =====================================================
      // ORDER ID
      // =====================================================

      const customOrderId =

        liveOrder?._id ||

        liveOrder?.orderId ||

        orderDetails?.orderId ||

        orderDetails?._id ||

        safeMessage.customOrderId ||

        null;


      // =====================================================
      // CUSTOM CHECKOUT
      // =====================================================

      const handleCustomCheckout =
        () => {

          console.log(
            "========================================"
          );

          console.log(
            "🛒 CUSTOM ORDER CHECKOUT"
          );

          console.log(
            "Custom Order ID:",
            customOrderId
          );

          console.log(
            "Is Catering:",
            isCateringOrder
          );

          console.log(
            "Menu Items:",
            items
          );

          console.log(
            "Guests:",
            cateringGuests
          );

          console.log(
            "Function:",
            cateringFunctionName
          );

          console.log(
            "Function Date:",
            cateringFunctionDate
          );

          console.log(
            "========================================"
          );


          // ===============================================
          // ID SAFETY
          // ===============================================

          if (!customOrderId) {

            setOrderActionError(
              "Custom order ID is missing."
            );

            return;

          }


          // ===============================================
          // ITEM SAFETY
          // ===============================================

          if (
            !Array.isArray(items) ||
            items.length === 0
          ) {

            console.error(
              "❌ CHECKOUT BLOCKED - NO ITEMS",
              {

                liveOrder,

                orderDetails,

                cateringDetails,

                candidates: {

                  liveMenuItems:
                    liveOrder?.menuItems,

                  liveCateringMenuItems:
                    liveOrder?.cateringDetails?.menuItems,

                  liveCustomMenuItems:
                    liveOrder?.customOrderDetails?.menuItems,

                  messageMenuItems:
                    orderDetails?.menuItems,

                  messageCateringMenuItems:
                    orderDetails?.cateringDetails?.menuItems,

                  messageCustomMenuItems:
                    orderDetails?.customOrderDetails?.menuItems,

                },

              }
            );


            setOrderActionError(

              isCateringOrder

                ? "No catering menu items found in this order."

                : "No items found in this custom order."

            );


            return;

          }


          // ===============================================
          // CATERING CHECKOUT
          // ===============================================

          if (isCateringOrder) {

            const acceptedOrder = {

              // -------------------------------------------
              // IDENTITY
              // -------------------------------------------

              orderId:
                customOrderId,


              providerType:
                "Catering",


              orderType:
                "catering",


              // -------------------------------------------
              // CATERING
              // -------------------------------------------

              functionName:
                cateringFunctionName,


              functionDate:
                cateringFunctionDate,


              guests:
                cateringGuests,


              cateringDetails: {

                functionName:
                  cateringFunctionName,


                functionDate:
                  cateringFunctionDate,


                guests:
                  cateringGuests,


                menuItems:
                  items,

              },


              // -------------------------------------------
              // MENU ITEMS
              // -------------------------------------------

              menuItems:
                items,


              // -------------------------------------------
              // ALSO KEEP ITEMS
              //
              // This allows Checkout and OrderSummary
              // to use either architecture.
              // -------------------------------------------

              items:
                items,


              // -------------------------------------------
              // PRICING
              // -------------------------------------------

              subTotal:
                subtotal,


              gst:
                gst,


              packingCharge:
                packingCharge,


              deliveryCharge:
                deliveryCharge,


              platformFee:
                platformFee,


              discount:
                discount,


              totalAmount:
                grandTotal,


              grandTotal:
                grandTotal,


              // -------------------------------------------
              // CHAT
              // -------------------------------------------

              messageId:
                safeMessage._id,

            };


            console.log(
              "📤 CATERING ACCEPTED ORDER:",
              acceptedOrder
            );


            navigate(
              "/cart",
              {

                state: {

                  acceptedOrder:

                    acceptedOrder,

                },

              }
            );


            return;

          }


          // ===============================================
          // NORMAL CUSTOM ORDER
          // ===============================================

          const acceptedOrder = {

            orderId:
              customOrderId,


            providerType:
              liveOrder?.providerType ||
              orderDetails?.providerType ||
              "",


            orderType:
              "custom",


            items:
              items,


            subTotal:
              subtotal,


            gst:
              gst,


            packingCharge:
              packingCharge,


            deliveryCharge:
              deliveryCharge,


            platformFee:
              platformFee,


            discount:
              discount,


            totalAmount:
              grandTotal,


            grandTotal:
              grandTotal,


            messageId:
              safeMessage._id,

          };


          console.log(
            "📤 NORMAL CUSTOM ACCEPTED ORDER:",
            acceptedOrder
          );


          navigate(
            "/cart",
            {

              state: {

                acceptedOrder:
                  acceptedOrder,

              },

            }
          );

        };


      // =====================================================
      // RETURN CARD
      // =====================================================

      return (

        <div
          className={`custom-order-card ${
            isAccepted
              ? "custom-order-card-accepted"
              : ""
          } ${
            isRejected
              ? "custom-order-card-rejected"
              : ""
          }`}
        >

          {/* =========================================
              HEADER
          ========================================= */}

          <div className="custom-order-card-header">

            <div className="custom-order-card-icon">

              <MdShoppingCartCheckout />

            </div>


            <div className="custom-order-card-title">

              <h4>

                {isCateringOrder
                  ? "Catering Order"
                  : "Custom Order"}

              </h4>


              <span>

                {isCateringOrder
                  ? "HomeFoods Catering Order"
                  : "HomeFoods Custom Order"}

              </span>

            </div>

          </div>


          {/* =========================================
              LOADING
          ========================================= */}

          {customOrderLoading &&
            !liveOrder && (

              <div className="custom-order-live-loading">

                Checking latest order status...

              </div>

            )}


          {/* =========================================
              CATERING DETAILS
          ========================================= */}

          {isCateringOrder && (

            <div className="custom-order-catering-details">

              {cateringFunctionName && (

                <div>

                  <span>
                    Function
                  </span>


                  <strong>
                    {cateringFunctionName}
                  </strong>

                </div>

              )}


              {cateringFunctionDate && (

                <div>

                  <span>
                    Date
                  </span>


                  <strong>

                    {new Date(
                      cateringFunctionDate
                    ).toLocaleDateString(
                      "en-IN",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }
                    )}

                  </strong>

                </div>

              )}


              <div>

                <span>
                  Guests
                </span>


                <strong>
                  {cateringGuests}
                </strong>

              </div>

            </div>

          )}


          {/* =========================================
              ITEMS
          ========================================= */}

          <div className="custom-order-items">

            {items.map(
              (
                item,
                index
              ) => {

                const quantity =

                  isCateringOrder

                    ? 1

                    : (
                      Number(
                        item?.quantity
                      ) || 1
                    );


                const price =
                  Number(
                    item?.price ??
                    item?.offerPrice ??
                    0
                  ) || 0;


                const itemTotal =

                  isCateringOrder

                    ? price

                    : (
                      Number(
                        item?.total
                      ) ||
                      (
                        price *
                        quantity
                      )
                    );


                const itemName =

                  item?.itemName ||

                  item?.foodName ||

                  item?.name ||

                  item?.menuItemName ||

                  item?.foodId?.foodName ||

                  item?.foodId?.name ||

                  "Custom Item";


                return (

                  <div
                    className="custom-order-item"
                    key={
                      item?._id ||
                      item?.foodId?._id ||
                      item?.foodId ||
                      `${itemName}-${index}`
                    }
                  >

                    <div className="custom-order-item-number">

                      {index + 1}

                    </div>


                    <div className="custom-order-item-info">

                      <strong>
                        {itemName}
                      </strong>


                      <span>

                        {quantity}{" "}

                        {item?.unit ||
                          "item"}

                      </span>

                    </div>


                    {(isPriceAdded ||
                      isAccepted) &&
                      price > 0 && (

                        <div className="custom-order-item-price">

                          ₹
                          {itemTotal.toFixed(2)}

                        </div>

                      )}

                  </div>

                );

              }
            )}

          </div>


          {/* =========================================
              NO ITEMS DEBUG
          ========================================= */}

          {items.length === 0 && (

            <div className="custom-order-error">

              {isCateringOrder
                ? "No catering menu items are available yet."
                : "No custom order items are available yet."}

            </div>

          )}


          {/* =========================================
              SPECIAL INSTRUCTIONS
          ========================================= */}

          {(
            liveOrder?.customOrderDetails
              ?.specialInstructions ||

            liveOrder?.cateringDetails
              ?.specialInstructions ||

            cateringDetails
              ?.specialInstructions ||

            orderDetails
              ?.specialInstructions

          ) && (

            <div className="custom-order-instructions">

              <strong>
                Special Instructions
              </strong>


              <p>

                {
                  liveOrder?.customOrderDetails
                    ?.specialInstructions ||

                  liveOrder?.cateringDetails
                    ?.specialInstructions ||

                  cateringDetails
                    ?.specialInstructions ||

                  orderDetails
                    ?.specialInstructions
                }

              </p>

            </div>

          )}


          {/* =========================================
              STATUS
          ========================================= */}

          <div className="custom-order-status">

            {isPending && (

              <>

                <span
                  className="custom-status-dot"
                />


                <span>
                  Pending Provider Review
                </span>

              </>

            )}


            {isReviewing && (

              <>

                <span
                  className="custom-status-dot reviewing"
                />


                <span>
                  Provider is reviewing your request
                </span>

              </>

            )}


            {isPriceAdded && (

              <>

                <MdEditNote />


                <span>
                  Price Added — Awaiting Your Approval
                </span>

              </>

            )}


            {isAccepted && (

              <>

                <MdCheckCircle />


                <span>
                  Custom Order Accepted
                </span>

              </>

            )}


            {isRejected && (

              <>

                <MdCancel />


                <span>
                  Custom Order Rejected
                </span>

              </>

            )}

          </div>


          {/* =========================================
              PRICE BREAKDOWN
          ========================================= */}

          {(isPriceAdded ||
            isAccepted) &&
            liveOrder && (

              <div className="custom-order-pricing">

                <div>

                  <span>
                    Subtotal
                  </span>


                  <strong>
                    ₹{subtotal.toFixed(2)}
                  </strong>

                </div>


                {gst > 0 && (

                  <div>

                    <span>
                      GST
                    </span>


                    <strong>
                      ₹{gst.toFixed(2)}
                    </strong>

                  </div>

                )}


                {packingCharge > 0 && (

                  <div>

                    <span>
                      Packing
                    </span>


                    <strong>
                      ₹{packingCharge.toFixed(2)}
                    </strong>

                  </div>

                )}


                {deliveryCharge > 0 && (

                  <div>

                    <span>
                      Delivery
                    </span>


                    <strong>
                      ₹{deliveryCharge.toFixed(2)}
                    </strong>

                  </div>

                )}


                {platformFee > 0 && (

                  <div>

                    <span>
                      Platform Fee
                    </span>


                    <strong>
                      ₹{platformFee.toFixed(2)}
                    </strong>

                  </div>

                )}


                {discount > 0 && (

                  <div>

                    <span>
                      Discount
                    </span>


                    <strong
                      className="custom-order-discount"
                    >

                      - ₹
                      {discount.toFixed(2)}

                    </strong>

                  </div>

                )}


                <div
                  className="custom-order-total"
                >

                  <span>
                    Grand Total
                  </span>


                  <strong>
                    ₹{grandTotal.toFixed(2)}
                  </strong>

                </div>

              </div>

            )}


          {/* =========================================
              PROVIDER REVIEW
          ========================================= */}

          {isProvider &&
            (
              isPending ||
              isReviewing
            ) && (

              <button
                type="button"
                className="custom-order-review-button"
                onClick={() => {

                  if (!customOrderId) {

                    setOrderActionError(
                      "Custom order ID is missing."
                    );

                    return;

                  }


                  navigate(
                    `/business/custom-orders/${customOrderId}`
                  );

                }}
              >

                <MdEditNote />


                Review & Price Order


                <MdArrowForward />

              </button>

            )}


          {/* =========================================
              CUSTOMER - PRICE ADDED
          ========================================= */}

          {isCustomer &&
            isPriceAdded && (

              <button
                type="button"
                className="custom-order-checkout-button"
                onClick={
                  handleCustomCheckout
                }
              >

                <span>

                  {isCateringOrder
                    ? "Review Catering Price & Checkout"
                    : "Review Price & Checkout"}

                </span>


                <MdArrowForward />

              </button>

            )}


          {/* =========================================
              CUSTOMER - ACCEPTED
          ========================================= */}

          {isCustomer &&
            isAccepted && (

              <button
                type="button"
                className="custom-order-checkout-button"
                onClick={
                  handleCustomCheckout
                }
              >

                <span>

                  {isCateringOrder
                    ? "Proceed to Catering Checkout"
                    : "Proceed to Checkout"}

                </span>


                <MdArrowForward />

              </button>

            )}


          {/* =========================================
              CUSTOMER - REJECTED
          ========================================= */}

          {isCustomer &&
            isRejected && (

              <div className="customer-order-rejected">

                <MdCancel />


                <span>

                  Sorry, this custom order
                  was rejected by the provider.

                </span>

              </div>

            )}


          {/* =========================================
              ERROR
          ========================================= */}

          {orderActionError && (

            <div className="custom-order-error">

              {orderActionError}

            </div>

          )}

        </div>

      );

    };


  // =========================================================
  // MAIN MESSAGE CONTENT
  // =========================================================

  const renderContent =
    () => {

      switch (messageType) {

        // ===================================================
        // IMAGE
        // ===================================================

        case "image":

          return (

            <>

              {renderImageMessage()}


              {safeMessage.message && (

                <p className="message-text">

                  {safeMessage.message}

                </p>

              )}

            </>

          );


        // ===================================================
        // FOOD REQUEST
        // ===================================================

        case "food_request":

          return (

            <>

              {renderRequestMessage()}


              {safeMessage.message && (

                <p className="message-text">

                  {safeMessage.message}

                </p>

              )}

            </>

          );


        // ===================================================
        // CAKE REQUEST
        // ===================================================

        case "cake_request":

          return (

            <>

              {renderRequestMessage()}


              {safeMessage.message && (

                <p className="message-text">

                  {safeMessage.message}

                </p>

              )}

            </>

          );


        // ===================================================
        // CATERING REQUEST
        // ===================================================

        case "catering_request":

          return (

            <>

              {renderRequestMessage()}


              {safeMessage.message && (

                <p className="message-text">

                  {safeMessage.message}

                </p>

              )}

            </>

          );


        // ===================================================
        // PROVISION REQUEST
        // ===================================================

        case "provision_request":

          return (

            <>

              {renderRequestMessage()}


              {safeMessage.message && (

                <p className="message-text">

                  {safeMessage.message}

                </p>

              )}

            </>

          );


        // ===================================================
        // OFFER
        // ===================================================

        case "offer":

          return renderOfferMessage();


        // ===================================================
        // CUSTOM ORDER REQUEST
        // ===================================================

        case "custom_order_request":

          return renderCustomOrderMessage();


        // ===================================================
        // CUSTOM ORDER PRICE
        // ===================================================

        case "custom_order_price":

          return renderCustomOrderMessage();


        // ===================================================
        // CUSTOM ORDER ACCEPTED
        // ===================================================

        case "custom_order_accepted":

          return renderCustomOrderMessage();


        // ===================================================
        // CUSTOM ORDER REJECTED
        // ===================================================

        case "custom_order_rejected":

          return renderCustomOrderMessage();


        // ===================================================
        // NORMAL ORDER REQUEST
        // ===================================================

        case "order_request":

          return renderOrderMessage();


        // ===================================================
        // SYSTEM
        // ===================================================

        case "system":

          return (

            <div className="system-message-content">

              {safeMessage.message}

            </div>

          );


        // ===================================================
        // NORMAL TEXT
        // ===================================================

        default:

          return (

            <p className="message-text">

              {safeMessage.message}

            </p>

          );

      }

    };


  // =========================================================
  // SYSTEM MESSAGE
  // =========================================================

  if (
    messageType === "system"
  ) {

    return (

      <div className="message-system-wrapper">

        <div className="message-system">

          {renderContent()}

        </div>

      </div>

    );

  }


  // =========================================================
  // NORMAL MESSAGE
  // =========================================================

  return (

    <div
      className={`message-row ${
        isOwnMessage
          ? "message-own"
          : "message-other"
      }`}
    >

      <div className="message-bubble">

        {/* CONTENT */}

        {renderContent()}


        {/* META */}

        <div className="message-meta">

          <span className="message-time">

            {formatTime(
              safeMessage.createdAt
            )}

          </span>


          {renderReadStatus()}

        </div>

      </div>

    </div>

  );

}


export default Message;